import React from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px); /* Account for progress bar */
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  overflow-y: auto; /* Enable scrolling within the container */
  overflow-x: hidden; /* Prevent horizontal scrolling */
  width: 100%;
  max-width: 100%;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    height: calc(100vh - 4px); /* Account for progress bar height */
    padding-bottom: calc(${theme.spacing.screenPadding} + 250px); /* Extra bottom padding to ensure CTA is fully visible */
    /* Ensure proper scrolling on mobile with fixed parent container */
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  /* Remove justify-content: center to prevent content from being pushed down */
  max-width: 100%;
  min-height: 0; /* Allow content to shrink */
  width: 100%;
  overflow-x: hidden; /* Prevent horizontal overflow */
`;

const ImageContainer = styled.div`
  margin: ${theme.spacing.lg} 0;
  text-align: center;
`;

const Image = styled.img`
  max-width: 30%;
  height: auto;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  
  /* Scale down more gradually as screen gets smaller */
  @media (max-width: ${theme.breakpoints.desktop}) {
    max-width: 35%;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 40%;
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    max-width: 45%;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 50%;
  }
`;

const ArticleInfo = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
  max-width: 100%;
`;

const ArticleCategory = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
  text-transform: uppercase;
  letter-spacing: ${theme.typography.letterSpacing.wide};
  margin-bottom: ${theme.spacing.sm};
`;

const ArticleTitle = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
  line-height: ${theme.typography.lineHeights.tight};
`;

const ArticleSubheading = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: 0;
`;

const TextContent = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
  
  p {
    margin-bottom: ${theme.spacing.md};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* Support for HTML tags */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
  
  /* Line breaks work naturally, no special CSS needed */
  
  @media screen and (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: ${theme.typography.sizes.body};
  }
`;

const ReadArticleButton = styled.a`
  display: block;
  background: transparent;
  color: ${theme.colors.primary};
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  text-decoration: none;
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  width: 30%; /* Same width as the image */
  text-align: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  margin: 0 auto ${theme.spacing.xl} auto; /* Center the button */
  
  /* Scale down proportionally with the image */
  @media (max-width: ${theme.breakpoints.desktop}) {
    width: 35%; /* Same width as 35% image */
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 40%; /* Same width as 40% image */
  }
  
  @media (max-width: ${theme.breakpoints.mobileLg}) {
    width: 45%; /* Same width as 45% image */
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 50%; /* Same width as 50% image */
  }
  
  &:hover {
    background: ${theme.colors.primary};
    color: white;
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.md} ${theme.spacing.lg};
  }
`;

const ContinueButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px; /* Ensure good touch target size */
  box-shadow: ${theme.shadows.sm};
  /* Button is now in content flow, adjust spacing */
  margin-top: 0;
  margin-bottom: 24px;
  
  /* Mobile-specific margin to ensure visibility */
  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-bottom: 40px; /* Extra margin on mobile to ensure button is fully visible */
  }
  /* Better width handling to prevent horizontal scroll */
  width: 100%;
  max-width: 300px;
  align-self: center;
  /* Multi-line text support for very long text */
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  /* Ensure proper touch targets on mobile */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
    max-width: 280px; /* Slightly smaller max width on mobile */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
    max-width: 250px; /* Even smaller max width on very small screens */
  }
`;

const AsSeenInScreen = ({ data, onNext, userProgress = {} }) => {
  const handleContinue = () => {
    onNext?.(data.key);
  };

  // Get formatted HTML content - combine all text content into one field
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  return (
    <Container>
      <Content>
        {/* Brand Image */}
        {data.content?.brandImage && (
          <ImageContainer>
            <Image 
              src={data.content.brandImage} 
              alt={data.content?.brandName || 'Brand'} 
            />
          </ImageContainer>
        )}
        
        {/* Read Article Button */}
        {data.content?.articleUrl && (
          <ReadArticleButton 
            href={data.content.articleUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Read Article
          </ReadArticleButton>
        )}
        
        {/* Article Information */}
        {(data.content?.category || data.content?.headline || data.content?.subheading) && (
          <ArticleInfo>
            {data.content?.category && (
              <ArticleCategory>{data.content.category}</ArticleCategory>
            )}
            {data.content?.headline && (
              <ArticleTitle>{data.content.headline}</ArticleTitle>
            )}
            {data.content?.subheading && (
              <ArticleSubheading>{data.content.subheading}</ArticleSubheading>
            )}
          </ArticleInfo>
        )}
        
        {/* Screen description text moved below Read Article button */}
        {htmlContent && (
          <TextContent dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {/* CTA button appears right after content */}
        <ContinueButton onClick={handleContinue}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
      </Content>
    </Container>
  );
};

export default AsSeenInScreen;
